import type { BetEvent, BoardProperty, Outcomes } from '../../@types/Board.js';
import type { ITSPayloadBoard } from '../../@types/ITSPayload.js';
import type { SystemType } from '../../@types/SystemType.js';
import { WagerError } from '../../error/WagerError.js';
import { asStringArray } from '../../utils/asStringArray.js';
import BoardPoolgames from './BoardPoolgames.js';

const calculateMsystem = (a: number): number => a;
const getMaltipsetRSystem = (a: number): number => a;

export type ITSNumboardsBoard = ITSPayloadBoard<{
  rowPriceMulti: number;
  boardData: (number | undefined)[];
  numSelections: number;
  participation: string;
  systemNum: number;
  boardProps: readonly BoardProperty[];
  systemType: SystemType[];
}>;

class BoardNumboards extends BoardPoolgames {
  /**
   * Resolve system type for Måltipset.
   *
   * @returns System type for the bet
   */
  override get systemType(): SystemType {
    const numSelections = this.getBoardData().length;
    const [min, max] = this.productConfig.eventBounds;

    if (numSelections > min && numSelections <= max) {
      if (!this.systemNumber) {
        return 'MSYS';
      }

      if (this.systemNumber) {
        return 'RSYS';
      }
    }

    return super.systemType;
  }

  /**
   * Simplifying add bet selection of outcomes to the bet for Måltipset.
   *
   * @param outcomeGroups Outcome selections for the bet.
   * @returns The updated Bet instance.
   * @throws {WagerError} If the outcome selection is invalid.
   */
  override addOutcomes(...outcomeGroups: Outcomes | BetEvent): this {
    // if (!Array.isArray(outcomes)) {
    //   throw new WagerError({ message: 'outcomes has to be an array', clientCode: WagerError.ErrorCodes.GENERIC });
    // }

    const [min, max] = this.productConfig.eventBounds;
    const outcomes = [...new Set(outcomeGroups.flat())];

    if (outcomes.length < min || outcomes.length > max) {
      const rem = outcomes.length - outcomes.length;

      let message = `${min} to ${max} outcomes is required. "${outcomes.join(', ')}"`;
      if (rem > 0) {
        message += `, ${rem} duplicates removed.`;
      }
      message += ` (length: ${outcomes.length}) is not valid.`;

      throw new WagerError({ message });
    }

    for (const selection of outcomes) {
      super.addOutcomes([selection]);
    }

    return this;
  }

  /**
   * Måltipset only has one level of boardData so we
   * resolve the outcomes to one array of selections.
   *
   * @returns Array with numbers
   */
  getBoardData(): (number | undefined)[] {
    return this.outcomes.map((selection) => {
      const nextSelection = selection.flat();

      return nextSelection[0];
    });
  }

  /**
   * Systems affect the number of rows so we override
   * here to resolve for each system.
   *
   * @returns Total number of rows
   */
  override getRowCount(): number {
    const numSelections = this.getBoardData().length;

    switch (this.systemType) {
      case 'MSYS':
        return calculateMsystem(numSelections);
      case 'RSYS':
        return getMaltipsetRSystem(numSelections);
      default:
        return super.getRowCount();
    }
  }

  /**
   * Takes systemNumber and if valid systemNumber it sets it for the board.
   *
   * @param systemNumber system number for given system
   * @returns the updated bet instance
   * @throws {WagerError} If systemNumber is invalid
   */
  override setSystemNumber(systemNumber: number): this {
    if (!getMaltipsetRSystem(systemNumber)) {
      throw new WagerError({
        message: `Unsupported systemNumber: ${systemNumber}`,
        clientCode: 'UNSUPPORTED_VALUE_SYSTEM_NUMBER',
      });
    }

    super.setSystemNumber(systemNumber);

    return this;
  }

  /**
   * Loops over outcomes to generate correct data for all outcomes.
   *
   * @returns outcomes as correct its structure string
   */
  composeOwnRows(): ITSPayloadBoard<string> {
    const outcomeString = this.outcomes
      .map((signArray) =>
        signArray.map(([sign]) => {
          return `0${sign}`.slice(-2);
        })
      )
      .join('');

    return outcomeString as ITSPayloadBoard<typeof outcomeString>;
  }

  /**
   * Loops over boards to generate correct data for all boards.
   *
   * @returns Object | Returns an object with correct data and structure for ITS-payment
   */
  compose(): ITSNumboardsBoard {
    const boardData = this.getBoardData();

    const isSystem = this.systemType === 'MSYS' || this.systemType === 'RSYS';

    const itsPayload = {
      rowPriceMulti: 1,
      boardData,
      numSelections: this.outcomes.length,
      // Used for terminal, need participation to be 0.
      participation: '0',
      systemNum: isSystem ? this.outcomes.length : 0,
      boardProps: this.boardProperties,
      systemType: [this.systemType],
    };

    type ITSBoardNumboards = ITSPayloadBoard<typeof itsPayload>;

    return itsPayload as ITSBoardNumboards;
  }

  /**
   * Used for tracking and logs.
   *
   * @returns String | String with boardData, boardProperties and systemType
   */
  override toString(): string {
    const logString = super.toString();

    const outcomeString = this.outcomes.map((board) => board.map((selection) => asStringArray(selection))).join(',');
    const logEntries = [`B:[${outcomeString}]`, logString];

    return logEntries.join(' ');
  }
}

export default BoardNumboards;
